import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import IdsElement from '../../core/ids-element';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import styles from './ids-media-viewer.scss';
import '../ids-image/ids-image';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';

export enum IdsMediaType {
  Video = 'video',
  Image = 'image'
}

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS media viewer component
 * @type {IdsMediaViewer}
 * @inherits IdsElement
 * @part viewer - the viewer container element
 */
@customElement('ids-media-viewer')
@scss(styles)
export default class IdsMediaViewer extends Base {
  constructor() {
    super();
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#attachEventHandlers();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.ALT,
      attributes.OBJECT_FIT,
      attributes.SRC,
      attributes.TYPE
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const src = this.src;
    const alt = this.alt;

    return `
      <div class="ids-media-viewer-zoom-container">
        <ids-image src="${src}" alt="${alt}" size="full"></ids-image>
        <video class="ids-media-viewer-video" controls width="100%" height="auto" hidden>
          <source src="${src}" type="video/mp4">
          <ids-text translate-text="true">Your browser does not support the video tag.</ids-text>
        </video>
      </div>
      <div id="ids-hover-zoom-container" class="ids-hover-zoom-container" hidden>
        <ids-image id="ids-image-hover" class="ids-image-hover" src="${src}" alt="${alt}" size="full"></ids-image>
      </div>
    `;
  }

  /**
   * Sets up event listeners
   * @private
   * @returns {void}
   */
  #attachEventHandlers() {}

  /**
   * Set the type of media between image or video
   * @param {string} value Trend label text
   */
  set type(value: string) {
    if (value) this.setAttribute(attributes.TYPE, value);
    else this.removeAttribute(attributes.TYPE);
  }

  get type(): string {
    return this.getAttribute(attributes.TYPE) || IdsMediaType.Image;
  }

  /**
   * Set the src url of the image
   * @param {string} value src url
   */
  set src(value: string) {
    if (value) {
      this.setAttribute(attributes.SRC, value);
      this.shadowRoot?.querySelector('ids-image')?.setAttribute(attributes.SRC, value);
    } else {
      this.removeAttribute(attributes.SRC);
      this.shadowRoot?.querySelector('ids-image')?.removeAttribute(attributes.SRC);
    }
  }

  get src(): string {
    return this.getAttribute(attributes.SRC) || '';
  }

  /**
   * Set the alt text of the image or video
   * @param {string} value of alt text
   */
  set alt(value: string) {
    if (value) {
      this.setAttribute(attributes.ALT, value);
      this.shadowRoot?.querySelector('ids-image')?.setAttribute(attributes.ALT, value);
    } else {
      this.removeAttribute(attributes.SRC);
      this.shadowRoot?.querySelector('ids-image')?.removeAttribute(attributes.ALT);
    }
  }

  get alt(): string {
    return this.getAttribute(attributes.ALT) || '';
  }

  /** Valid object-fit values */
  static OBJECT_FIT_VALUES = ['contain', 'cover', 'fill', 'none', 'scale-down'];

  /**
   * Set the object-fit style for the image.
   * CSS handles the actual styling via :host([object-fit]) selectors.
   * @param {string} value - One of: contain, cover, fill, none, scale-down
   */
  set objectFit(value: string) {
    if (value && IdsMediaViewer.OBJECT_FIT_VALUES.includes(value)) {
      this.setAttribute(attributes.OBJECT_FIT, value);
    } else {
      this.removeAttribute(attributes.OBJECT_FIT);
    }
  }

  get objectFit(): string {
    return this.getAttribute(attributes.OBJECT_FIT) || 'contain';
  }
}
